/**
 * PATH: packages/auth/src/identityReconcile.ts
 * FILE_ID: AUTH-IDENTITY-RECONCILE-001
 * PURPOSE: Pure verdict primitive that answers ONE question — does the
 *          access token this tab is about to trust actually belong to
 *          the persisted/cached user the app is about to render as?
 * WHY:     Every existing identity guard fires on a DISAGREEMENT that has
 *          already surfaced at runtime (cross-tab `storage` event, a
 *          server `X-User-Id` response header, a refreshed token whose
 *          `sub` flipped). None of them run at the COLD-LOAD / auto-login
 *          seam, where a stale persisted `user` (e.g. dev@quotech.ai) is
 *          adopted alongside a token/cookie that the user actually signed
 *          in with for a DIFFERENT account. This primitive lets the
 *          auto-login + SSO paths compare the two BEFORE adopting the
 *          stale identity. See BUG-2026-06-02-COLD-LOAD-IDENTITY-ADOPTION.
 * FLOW:
 *   ┌────────────────────┐   reuse    ┌──────────────┐   compare   ┌─────────────┐
 *   │ token + cached user │ ────────► │ decodeJwtSub │ ─────────► │ match /     │
 *   │ (inputs)            │           │ (existing)   │            │ mismatch /  │
 *   └────────────────────┘           └──────────────┘            │ indeterm.   │
 *                                                                 └─────────────┘
 * DEPENDENCIES:
 *   - ./session-manager/tokenSync.decodeJwtSub: the SINGLE existing
 *     JWT `sub` extractor (lowercased, null-safe). Reused, not rewritten,
 *     so this primitive stays bug-for-bug consistent with the axios
 *     silent-refresh sub-mismatch guard that already ships.
 * RELATED:
 *   - axiosSetup.ts (master src/reducers): the runtime sub-mismatch guard
 *     on the 401 refresh path — same `sub` comparison, different seam.
 *   - tabIdentity.ts: the server-asserted (`X-User-Id`) per-tab pin —
 *     authoritative once a response lands; this primitive is the
 *     pre-request, client-only backstop for the cold-load gap.
 */

import { decodeJwtSub } from './session-manager/tokenSync'

/**
 * Verdict of comparing a token's subject to a cached user identity.
 *
 *  - `match`         — the token's `sub` equals the cached user's id or
 *                      email. Safe to adopt the cached user.
 *  - `mismatch`      — the token decodes to a DIFFERENT identity than the
 *                      cached user. The cached user is stale and MUST NOT
 *                      be rendered; callers should drop it and re-fetch
 *                      the profile from the token.
 *  - `indeterminate` — not enough information to decide (token undecodable,
 *                      or no cached identity to compare against). Callers
 *                      MUST treat this as "do nothing / let the existing
 *                      flow proceed" — NEVER as a reason to force a logout.
 *                      This deliberately preserves the 2026-05-19
 *                      false-positive lesson: an undecodable token is a
 *                      decode failure, not a confirmed identity change.
 */
export type IdentityMatchVerdict = 'match' | 'mismatch' | 'indeterminate'

/** Minimal shape this primitive needs from a cached/persisted user.
 *  Intentionally looser than `User` so callers can pass partial/legacy
 *  records (Redux `UserData`, Zustand `user`, a profile response) without
 *  a cast. */
export interface CachedIdentityLike {
  id?: string | number | null
  user_id?: string | number | null
  email?: string | null
}

/** Normalise an identifier (id or email) to the same canonical form
 *  `decodeJwtSub` returns: trimmed, lowercased string, or null when the
 *  value carries no usable identity. Numbers are stringified so a numeric
 *  `user.id` can compare against a `sub` claim that arrived as a string. */
export function normaliseIdentityValue(value: unknown): string | null {
  if (value === null || value === undefined) return null
  if (typeof value === 'number') {
    return Number.isFinite(value) ? String(value).trim().toLowerCase() : null
  }
  if (typeof value === 'string') {
    const trimmed = value.trim().toLowerCase()
    return trimmed.length > 0 ? trimmed : null
  }
  return null
}

/** Collect every candidate identifier a token `sub` could legitimately
 *  equal for a given cached user. Tokens in this platform carry the user
 *  id in `sub` for some issuers and the email for others, so a genuine
 *  same-user match can land on EITHER — we accept both. */
export function identityCandidates(user: CachedIdentityLike | null | undefined): string[] {
  if (!user || typeof user !== 'object') return []
  const out: string[] = []
  for (const raw of [user.id, user.user_id, user.email]) {
    const norm = normaliseIdentityValue(raw)
    if (norm && !out.includes(norm)) out.push(norm)
  }
  return out
}

export interface ReconcileResult {
  verdict: IdentityMatchVerdict
  /** The token's decoded subject (lowercased) or null when undecodable. */
  tokenSub: string | null
  /** The cached identifiers the token was compared against. */
  candidates: string[]
}

/**
 * Pure reconciliation: does `token` belong to `user`?
 *
 * Decision table (token has been decoded to `sub`):
 *   sub == null                      → indeterminate (can't decide)
 *   no cached candidates             → indeterminate (nothing to compare)
 *   sub ∈ candidates                 → match
 *   otherwise                        → mismatch
 *
 * This function has NO side effects and reads NO globals — it is fully
 * testable in isolation and safe to call from any auth seam.
 */
export function reconcileIdentity(input: {
  token?: string | null
  user?: CachedIdentityLike | null
}): ReconcileResult {
  const tokenSub = decodeJwtSub(input.token)
  const candidates = identityCandidates(input.user)

  if (tokenSub === null) {
    return { verdict: 'indeterminate', tokenSub, candidates }
  }
  if (candidates.length === 0) {
    return { verdict: 'indeterminate', tokenSub, candidates }
  }
  return {
    verdict: candidates.includes(tokenSub) ? 'match' : 'mismatch',
    tokenSub,
    candidates,
  }
}

/** Convenience wrapper returning just the verdict — for call sites that
 *  only need to branch on match/mismatch/indeterminate. */
export function tokenMatchesUser(
  token: string | null | undefined,
  user: CachedIdentityLike | null | undefined,
): IdentityMatchVerdict {
  return reconcileIdentity({ token, user }).verdict
}
